import { useState, useEffect } from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import * as Sentry from "@sentry/react";
import { supabaseClient } from './supabase.ts';
import { signOutLocally } from './impersonation';
import { identifyProductUser, resetProductUser } from './productAnalytics';
import type { AppUser, UseAuthReturn } from '../types/api';

/**
 * Custom React hook for managing authentication state with enhanced user context.
 * 
 * IMPORTANT: identity comes from the JWT, not from session.user
 * ==============================================================
 *
 * 1. BACKEND: our custom access token hook (access_token_hook_add_user_context)
 *    reads core_crm.app_user and writes it into the token on every issue:
 *    ```
 *    "app_metadata": {
 *      "core_crm.app_user": {
 *        "id": 4,
 *        "name": "Shmuel Kamensky",
 *        "email": "shmuelkamensky@gmail.com",
 *        "is_super_admin": true
 *      }
 *    }
 *    ```
 *
 * 2. SUPABASE CLIENT: session.user.app_metadata is NOT those claims. It is the
 *    auth.users row's raw_app_meta_data, which the hook never writes to. So the
 *    hook's output is only visible by decoding session.access_token ourselves.
 *
 * That is why this hook decodes the JWT rather than reading session.user. It is not
 * a workaround for a client bug, and it should not be "fixed" by reading
 * app_metadata off the session — see REG-541.
 */

export const useAuth = (): UseAuthReturn => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [appUser, setAppUser] = useState<AppUser | null>(null);

    /**
     * Extracts app_user data from session, with fallback to direct JWT decoding.
     * 
     * @param {Object} session - The Supabase session object
     * @returns {Object|null} The app_user data or null if not found
     */
    const extractAppUserFromSession = (session: Session | null): AppUser | null => {
        // Decode the JWT directly: session.user.app_metadata is the auth.users row's
        // raw_app_meta_data, and only the token carries the hook's claims.
        if (session?.access_token) {
            try {
                const payload = JSON.parse(atob(session.access_token.split('.')[1]));
                // The access_token_hook stores under 'core_crm.app_user' (dotted key)
                const appUserFromHook = payload.app_metadata?.['core_crm.app_user'];
                if (appUserFromHook && appUserFromHook.id) {
                    return appUserFromHook;
                }
            } catch (error) {
                console.error('Error decoding JWT token:', error);
            }
        }

        // No fallback to flat app_metadata fields on purpose. Those were a copy of
        // core_crm.app_user that nothing kept current, so falling back to them could
        // hand the UI another user's id, or an is_super_admin the user never had.
        return null;
    };

    /**
     * Update Sentry user context
     */
    const updateSentryUser = (currentUser: User | null, currentAppUser: AppUser | null) => {
        if (currentUser && currentAppUser) {
            identifyProductUser({
                authUserId: currentUser.id,
                appUserId: currentAppUser.id,
                name: currentAppUser.name,
                email: currentAppUser.email,
                isSuperAdmin: currentAppUser.is_super_admin,
            });
            Sentry.setUser({
                id: currentUser.id,
                email: currentAppUser.email,
                username: currentAppUser.name,
                ip_address: '{{auto}}', // Let Sentry infer IP
                // Add custom segments for filtering
                segment: currentAppUser.is_super_admin ? 'admin' : 'user',
                data: {
                    app_user_id: currentAppUser.id,
                    is_super_admin: currentAppUser.is_super_admin
                }
            });
        } else if (currentUser) {
            // Fallback if appUser not loaded yet
            Sentry.setUser({
                id: currentUser.id,
                email: currentUser.email,
                ip_address: '{{auto}}'
            });
        } else {
            resetProductUser();
            // Clear user when logged out
            Sentry.setUser(null);
        }
    };

    useEffect(() => {
        // Get initial session
        const getInitialSession = async () => {
            try {
                const { data: { session: initialSession }, error } = await supabaseClient.auth.getSession();
                if (error) {
                    console.error('Error getting session:', error);
                } else {
                    setSession(initialSession);
                    setUser(initialSession?.user || null);
                    
                    // Extract app_user data using the workaround function
                    const appUserData = extractAppUserFromSession(initialSession);
                    setAppUser(appUserData);
                    
                    // Set Sentry User Context
                    updateSentryUser(initialSession?.user || null, appUserData);
                }
            } catch (error) {
                console.error('Error in getInitialSession:', error);
            } finally {
                setLoading(false);
            }
        };

        getInitialSession();

        // Listen for auth state changes
        const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(
            (_event, session) => {
                setSession(session);
                setUser(session?.user || null);
                
                // Extract app_user data using the workaround function
                const appUserData = extractAppUserFromSession(session);
                setAppUser(appUserData);
                
                // Update Sentry User Context
                updateSentryUser(session?.user || null, appUserData);
                
                setLoading(false);
            }
        );

        return () => subscription.unsubscribe();
    }, []);

    const signOut = async (): Promise<{ error: AuthError | null }> => {
        const { error } = await signOutLocally();
        if (error) {
            console.error('Error signing out:', error);
        } else {
            resetProductUser();
            // Explicitly clear Sentry user on signout
            Sentry.setUser(null);
        }
        return { error };
    };

    return {
        user,
        session,
        appUser,
        loading,
        signOut,
        isAuthenticated: !!user,
        isAdmin: appUser?.is_super_admin || false
    };
};

export default useAuth;
